import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Card, Tag, Button, Select, Modal, Form, Input, message, Empty, Spin, Row, Col, Popconfirm,
} from "antd";
import { defectService, workOrderService } from "@iso/services";

const { Option } = Select;

const SEVERITY_COLOR = { minor: "blue", major: "orange", critical: "red" };
const DEFECT_STATUS_COLOR = {
  open: "red", in_progress: "orange", resolved: "green", closed: "default", cancelled: "default",
};
const WO_STATUS_COLOR = {
  open: "red", in_progress: "orange", on_hold: "gold", completed: "blue", verified: "green", reopened: "red",
};
const DEFECT_NEXT = ["open", "in_progress", "resolved", "closed", "cancelled"];
const WO_NEXT = ["open", "in_progress", "on_hold", "completed", "reopened"];

const label = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Maintenance tab on the asset passport: defects + work orders + return-to-service
 * (Phase 3). Raising a defect is open to any property member; managing work orders,
 * changing statuses and RTS are owner/admin/safety — the UI hides those for other
 * roles, and the backend enforces the same gate.
 */
const AssetMaintenance = (props) => {
  const { asset, visible, onStateChange } = props;
  const assetId = asset && asset.id;

  const activePermission = useSelector((state) => state.permission.activePermission);
  const canManage = ["owner", "admin", "safety"].includes(activePermission);

  const [loading, setLoading] = useState(false);
  const [defects, setDefects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [defectModal, setDefectModal] = useState(false);
  const [woModal, setWoModal] = useState(false);
  const [woFromDefect, setWoFromDefect] = useState(null);
  const [saving, setSaving] = useState(false);
  const [defectForm] = Form.useForm();
  const [woForm] = Form.useForm();

  const load = useCallback(() => {
    if (!assetId) return;
    setLoading(true);
    Promise.all([defectService.getList(assetId), workOrderService.getList(assetId)])
      .then(([d, w]) => {
        setDefects((d && d.defects) || []);
        setWorkOrders((w && w.work_orders) || []);
      })
      .catch(() => message.error("Failed to load maintenance records"))
      .finally(() => setLoading(false));
  }, [assetId]);

  useEffect(() => {
    if (visible && assetId) load();
  }, [visible, assetId, load]);

  // Any change here can move the asset's readiness state, so let the parent refresh.
  const afterMutation = (res) => {
    if (res && res.code === "0000") {
      load();
      if (onStateChange) onStateChange();
      return true;
    }
    message.error((res && res.message) || "Action failed");
    return false;
  };

  const submitDefect = () => {
    defectForm.validateFields().then((values) => {
      setSaving(true);
      defectService
        .create({ facility_id: assetId, ...values })
        .then((res) => {
          if (afterMutation(res)) {
            message.success("Defect raised");
            setDefectModal(false);
            defectForm.resetFields();
          }
        })
        .finally(() => setSaving(false));
    });
  };

  const submitWo = () => {
    woForm.validateFields().then((values) => {
      setSaving(true);
      workOrderService
        .create({ facility_id: assetId, defect_id: woFromDefect || undefined, ...values })
        .then((res) => {
          if (afterMutation(res)) {
            message.success("Work order created");
            setWoModal(false);
            woForm.resetFields();
            setWoFromDefect(null);
          }
        })
        .finally(() => setSaving(false));
    });
  };

  const changeDefectStatus = (id, status) =>
    defectService.updateStatus(id, status).then(afterMutation);
  const changeWoStatus = (id, status) =>
    workOrderService.updateStatus(id, status).then(afterMutation);
  const returnToService = (id) =>
    workOrderService.returnToService(id).then((res) => {
      if (afterMutation(res)) message.success("Asset returned to service");
    });

  if (asset && asset.category !== "machinery") {
    return <Empty description="Maintenance is available for machinery assets." />;
  }
  if (loading) {
    return <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>;
  }

  return (
    <div style={{ padding: "8px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <b style={{ fontSize: 15 }}>Defects</b>
        <Button type="primary" size="small" onClick={() => setDefectModal(true)}>
          Raise Defect
        </Button>
      </div>
      {defects.length === 0 ? (
        <Empty description="No defects" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        defects.map((d) => (
          <Card key={d.id} size="small" style={{ marginBottom: 8, borderRadius: 8 }}>
            <Row align="middle" gutter={[8, 8]}>
              <Col flex="auto">
                <Tag color={SEVERITY_COLOR[d.severity]}>{label(d.severity)}</Tag>
                <Tag color={DEFECT_STATUS_COLOR[d.status]}>{label(d.status)}</Tag>
                {d.source === "inspection" && <Tag>Pre-start</Tag>}
                <b style={{ marginLeft: 4 }}>{d.title}</b>
                {d.description && (
                  <div style={{ color: "rgba(0,0,0,0.55)", marginTop: 4, whiteSpace: "pre-line" }}>{d.description}</div>
                )}
              </Col>
              {canManage && (
                <Col>
                  <Select
                    size="small"
                    value={d.status}
                    style={{ width: 130 }}
                    onChange={(s) => changeDefectStatus(d.id, s)}
                  >
                    {DEFECT_NEXT.map((s) => <Option key={s} value={s}>{label(s)}</Option>)}
                  </Select>
                  <Button
                    size="small"
                    style={{ marginLeft: 6 }}
                    onClick={() => { setWoFromDefect(d.id); setWoModal(true); }}
                  >
                    New Work Order
                  </Button>
                </Col>
              )}
            </Row>
          </Card>
        ))
      )}

      <div style={{ margin: "20px 0 12px" }}>
        <b style={{ fontSize: 15 }}>Work Orders</b>
        {/* Work orders are always raised FROM a defect (via each defect's
            "New Work Order" action), so there is no standalone create here —
            this keeps the defect -> work order -> return-to-service loop intact. */}
      </div>
      {workOrders.length === 0 ? (
        <Empty description="No work orders — raise one from a defect above" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        workOrders.map((w) => (
          <Card key={w.id} size="small" style={{ marginBottom: 8, borderRadius: 8 }}>
            <Row align="middle" gutter={[8, 8]}>
              <Col flex="auto">
                <Tag color={WO_STATUS_COLOR[w.status]}>{label(w.status)}</Tag>
                {w.priority && <Tag>{label(w.priority)}</Tag>}
                <b style={{ marginLeft: 4 }}>{w.title}</b>
                {w.assigned_user_name && (
                  <span style={{ color: "rgba(0,0,0,0.55)", marginLeft: 8 }}>· {w.assigned_user_name}</span>
                )}
              </Col>
              {canManage && w.status !== "verified" && (
                <Col>
                  <Select
                    size="small"
                    value={w.status}
                    style={{ width: 130 }}
                    onChange={(s) => changeWoStatus(w.id, s)}
                  >
                    {WO_NEXT.map((s) => <Option key={s} value={s}>{label(s)}</Option>)}
                  </Select>
                  <Popconfirm
                    title="Return this asset to service? This closes the linked defect and clears the restriction."
                    okText="Return to service"
                    onConfirm={() => returnToService(w.id)}
                  >
                    <Button size="small" type="primary" style={{ marginLeft: 6 }}>
                      Return to Service
                    </Button>
                  </Popconfirm>
                </Col>
              )}
            </Row>
          </Card>
        ))
      )}

      <Modal
        title="Raise Defect"
        open={defectModal}
        onCancel={() => setDefectModal(false)}
        onOk={submitDefect}
        confirmLoading={saving}
        okText="Raise"
      >
        <Form form={defectForm} layout="vertical" initialValues={{ severity: "major" }}>
          <Form.Item name="title" label="Title" rules={[{ required: true, whitespace: true, message: "Title is required" }]}>
            <Input placeholder="e.g. Hydraulic leak on left arm" />
          </Form.Item>
          <Form.Item name="severity" label="Severity">
            <Select>
              <Option value="minor">Minor</Option>
              <Option value="major">Major (restricts the asset)</Option>
              <Option value="critical">Critical (Do Not Operate)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="What's wrong, and any detail for the repair" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="New Work Order"
        open={woModal}
        onCancel={() => { setWoModal(false); setWoFromDefect(null); }}
        onOk={submitWo}
        confirmLoading={saving}
        okText="Create"
      >
        <Form form={woForm} layout="vertical" initialValues={{ priority: "medium" }}>
          <Form.Item name="title" label="Title" rules={[{ required: true, whitespace: true, message: "Title is required" }]}>
            <Input placeholder="e.g. Replace hydraulic hose" />
          </Form.Item>
          <Form.Item name="priority" label="Priority">
            <Select>
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssetMaintenance;
